import { AccessCountries, Country } from 'domains/user/UserCountriesPermissions'

export const EU_GROUP_KEY = 'EU'

export interface CountryOption extends Country {
  isEu: boolean
}

export const flattenCountries = (accessCountries: AccessCountries): CountryOption[] => {
  return Object.entries(accessCountries)
    .flatMap(([group, countries]) =>
      countries.map((country) => ({ ...country, isEu: group === EU_GROUP_KEY })),
    )
    .sort((left, right) => left.name.localeCompare(right.name))
}

export const euCountryCodes = (options: CountryOption[]): string[] =>
  options.filter((option) => option.isEu).map((option) => option.isoCode)

export type TriState = 'checked' | 'unchecked' | 'indeterminate'

export const resolveEuState = (euCodes: string[], selected: Set<string>): TriState => {
  if (euCodes.length === 0) {
    return 'unchecked'
  }

  const selectedCount = euCodes.filter((code) => selected.has(code)).length

  if (selectedCount === 0) {
    return 'unchecked'
  }

  return selectedCount === euCodes.length ? 'checked' : 'indeterminate'
}

export const toggleSelectAll = (allValues: string[], selected: Set<string>): Set<string> => {
  const allSelected = allValues.length > 0 && allValues.every((value) => selected.has(value))

  if (allSelected) {
    const next = new Set(selected)

    allValues.forEach((value) => next.delete(value))
    return next
  }

  return new Set([...selected, ...allValues])
}

export const toggleValue = (value: string, selected: Set<string>): Set<string> => {
  const next = new Set(selected)

  if (next.has(value)) {
    next.delete(value)
  } else {
    next.add(value)
  }

  return next
}

export const toggleEuRegion = (
  euCodes: string[],
  selected: Set<string>,
  selectAll: boolean,
): Set<string> => {
  const next = new Set(selected)

  if (selectAll) {
    euCodes.forEach((code) => next.add(code))
  } else {
    euCodes.forEach((code) => next.delete(code))
  }

  return next
}
